<template>
    <div>
        <div v-if="isLoading" class="loading-container">
            <DxLoadIndicator />
        </div>
        <div v-else class="mensaje-container" :class="{ 'con-icono': true }">
            <i v-if="!tieneMasSucesos" class="messagebox-icon-warning fa-solid fa-triangle-exclamation"></i>
            <i v-if="tieneMasSucesos" class="messagebox-icon-question fa-solid fa-circle-question"></i>
            <label class="mensaje-label">{{ mensaje }}</label>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useIntl } from '@services/i18n';
import { useApi } from '@/.shared/.api/integration';
import { useModalBuilder } from '@/.shared/helpers/modal-building-helper';
import { getCurrentInstance, onMounted, ref, computed } from 'vue';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import DxLoadIndicator from 'devextreme-vue/load-indicator';

const props = defineProps<{ idSuceso: number }>();

const api = useApi();
const intl = useIntl();
const modalBuilder = useModalBuilder();

const isLoading = ref<boolean>(true);
const countResult = ref<number>(0);
const mensaje = ref<string>('');
let botonesEstandar: { okButton: any; cancelButton: any } | null = null;

const tieneMasSucesos = computed(() => countResult.value > 0);

onMounted(async () => {
    modalBuilder.setContext(getCurrentInstance()!);

    // Configurar botones de confirmación por defecto (se cambiarán si hay más sucesos)
    configurarBotonesConfirmacion();

    // Hacer la llamada GET al endpoint
    try {
        const apiCallChain = await api.prepareStandardApi("sucesoGetDetalle")(props.idSuceso);
        const baseURL = apiCallChain.axiosInstance.defaults.baseURL!;
        // Construir la URL del endpoint: GET /api/Commons/Suceso/{id}/count-sucesos-oscilo
        const url = `${baseURL}/api/Commons/Suceso/${props.idSuceso}/count-sucesos-oscilo`;
        const response = await apiCallChain.axiosInstance.get(url);
        countResult.value = parseInt(response.data?.toString() || '0', 10);
        
        // Configurar mensaje y botones según el resultado
        if (countResult.value > 0) {
            mensaje.value = intl.i18n.t("sucesos.popup_suceso_revisado.mensaje_hay_mas_sucesos");
            // Reemplazar botones de confirmación con botones múltiples
            configurarBotonesMultiples();
        } else {
            mensaje.value = intl.i18n.t("sucesos.popup_suceso_revisado.mensaje_confirmar_revisado");
            // Los botones de confirmación ya están configurados
        }
    } catch (error) {
        console.error('Error al obtener el conteo de sucesos oscilo:', error);
        toastError(intl.i18n.t('sucesos.popup_suceso_revisado.mensaje_error'));
        mensaje.value = intl.i18n.t('sucesos.popup_suceso_revisado.error_cargar');
        // Los botones de confirmación ya están configurados
    } finally {
        // Marcar como cargado cuando termine (éxito o error)
        isLoading.value = false;
    }
});

function configurarBotonesMultiples(): void {
    // Ocultar los botones estándar que ya fueron creados
    if (botonesEstandar) {
        botonesEstandar.okButton.update((b: any) => {
            b.visible = false;
        });
        botonesEstandar.cancelButton.update((b: any) => {
            b.visible = false;
        });
    }

    // Añadir botones personalizados directamente
    modalBuilder.addButton('marcarTodos', {
        text: intl.i18n.t("sucesos.popup_suceso_revisado.boton_marcar_todos"),
        type: 'default',
        stylingMode: 'contained',
        onClick: async () => {
            await marcarSucesoRevisado(true);
        }
    }, { toolbar: 'bottom', location: 'after' });

    modalBuilder.addButton('marcarSoloEste', {
        text: intl.i18n.t("sucesos.popup_suceso_revisado.boton_marcar_solo_este"),
        type: 'default',
        stylingMode: 'contained',
        onClick: async () => {
            await marcarSucesoRevisado(false);
        }
    }, { toolbar: 'bottom', location: 'after' });

    modalBuilder.addButton('cerrar', {
        text: intl.i18n.t("sucesos.popup_suceso_revisado.boton_cerrar"),
        type: 'normal',
        stylingMode: 'outlined',
        onClick: async () => {
            modalBuilder.close(false);
        }
    }, { toolbar: 'bottom', location: 'after' });
}

function configurarBotonesConfirmacion(): void {
    botonesEstandar = modalBuilder.standardButtons.addOkCancel();
    botonesEstandar.okButton.update((b: any) => {
        b.text = intl.i18n.t("sucesos.popup_suceso_revisado.boton_si");
        b.visible = true;
        b.onClick = async () => {
            await marcarSucesoRevisado(false);
        }
    });
    
    botonesEstandar.cancelButton.update((b: any) => {
        b.text = intl.i18n.t("sucesos.popup_suceso_revisado.boton_no");
        b.visible = true;
        b.onClick = async () => {
            modalBuilder.close(false);
        }
    });
}

async function marcarSucesoRevisado(marcarTodos: boolean): Promise<void> {
    try {
        // Preparar la llamada POST
        const apiCallChain = await api.prepareStandardApi("sucesoGetDetalle")(props.idSuceso);
        const baseURL = apiCallChain.axiosInstance.defaults.baseURL!;
        const url = `${baseURL}/api/Commons/Suceso/${props.idSuceso}/marcar-revisado`;
        
        // Hacer la llamada POST
        await apiCallChain.axiosInstance.post(url, {
            MarcarTodos: marcarTodos
        });
        
        toastSuccess(intl.i18n.t('sucesos.popup_suceso_revisado.mensaje_suceso_marcado_revisado'));
        modalBuilder.close(true);
    } catch (error: any) {
        console.error('Error al marcar suceso como revisado:', error);
        console.error('Detalles del error:', error?.response?.data || error?.message);
        toastError(intl.i18n.t('sucesos.popup_suceso_revisado.mensaje_error_marcar_revisado'));
    }
}
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

.mensaje-container {
    padding: 5px 1px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-height: 50px;
}

.mensaje-container.con-icono {
    justify-content: flex-start;
    gap: 12px;
}

.messagebox-icon-warning {
    width: 42px;
    height: 44px;
    font-size: 2.25rem;
    color: #ffc107;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.messagebox-icon-question {
    width: 42px;
    height: 44px;
    font-size: 2.25rem;
    color: #0d6efd;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.mensaje-label {
    font-size: 1rem;
    font-weight: normal;
    color: var(--text-primary-default, #333);
    text-align: left;
}

.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 50px;
    padding: 20px;
}
</style>
